// src/AudioSpectrum.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import SpectrumVisualizer from './AudioSpectrum/SpectrumVisualizer';
import FrequencyAnalyzer from './AudioSpectrum/FrequencyAnalyzer';
import AudioFileUploader from './AudioSpectrum/AudioFileUploader';
import ControlPanel from './AudioSpectrum/ControlPanel';
import { recordPageVisit } from './utils/pageVisitTracker';
import './AudioSpectrum.css';

const AudioSpectrum = ({ language, user, countryCode, userIp }) => {
  const [audioContext, setAudioContext] = useState(null);
  const [audioBuffer, setAudioBuffer] = useState(null);
  const [sourceNode, setSourceNode] = useState(null);
  const [analyserNode, setAnalyserNode] = useState(null);
  const [gainNode, setGainNode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('');
  
  // 스펙트럼 분석 관련 상태
  const [spectrumData, setSpectrumData] = useState(null);
  const [visualizationType, setVisualizationType] = useState('bars');
  const [fftSize, setFftSize] = useState(2048);
  
  // 디버깅 로그
  const [debugLog, setDebugLog] = useState([]);
  const addDebugLog = (message) => {
    console.log(message);
    setDebugLog(prev => [...prev, { time: new Date().toISOString(), message }]);
  };
  
  const userId = user ? user.uid : 'unknown';
  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';
  const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isMobileDevice = isMobile();
  
  // 페이지 방문 기록
  useEffect(() => {
    if (countryCode && countryCode !== 'UNKNOWN') {
      recordPageVisit('AudioSpectrum', {
        userId,
        userEmail: user ? user.email : 'unknown',
        userIp,
        countryCode,
        SERVER_ROOT,
        language,
        isMobileDevice
      });
    }
  }, [countryCode]);
  
  // 현재 시간 업데이트
  useEffect(() => {
    let timeUpdateInterval;
    
    if (isPlaying && audioContext && startTime) {
      timeUpdateInterval = setInterval(() => {
        const elapsed = audioContext.currentTime - startTime;
        setCurrentTime(Math.min(elapsed, duration));
        
        if (elapsed >= duration) {
          handleStop();
        }
      }, 100);
    }
    
    return () => {
      if (timeUpdateInterval) {
        clearInterval(timeUpdateInterval);
      }
    };
  }, [isPlaying, audioContext, startTime, duration]);
  
  // 오디오 그래프 초기화
  const initAudioGraph = useCallback(async (buffer) => {
    try {
      addDebugLog('오디오 그래프 초기화 시작');
      
      const context = new (window.AudioContext || window.webkitAudioContext)();
      
      if (context.state === 'suspended') {
        await context.resume();
      }
      
      // 분석기 노드 생성
      const analyser = context.createAnalyser();
      analyser.fftSize = fftSize;
      analyser.smoothingTimeConstant = 0.8;
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      
      // 게인 노드 생성
      const gain = context.createGain();
      gain.gain.value = volume;
      
      // 연결: analyser -> gain -> destination
      analyser.connect(gain);
      gain.connect(context.destination);
      
      setAudioContext(context);
      setAnalyserNode(analyser);
      setGainNode(gain);
      setAudioBuffer(buffer);
      setDuration(buffer.duration);
      setCurrentTime(0);
      
      addDebugLog(`오디오 그래프 초기화 완료 - 길이: ${buffer.duration}초`);
      
    } catch (error) {
      addDebugLog(`오디오 그래프 초기화 실패: ${error.message}`);
      setError('오디오 시스템을 초기화할 수 없습니다.');
    }
  }, [fftSize, volume]);
  
  // 파일 로드 핸들러
  const handleFileLoad = async (file) => {
    setIsLoading(true);
    setError(null);
    
    try {
      addDebugLog(`파일 로드 시작: ${file.name}`);
      
      const arrayBuffer = await file.arrayBuffer();
      const context = new AudioContext();
      const buffer = await context.decodeAudioData(arrayBuffer);
      
      setFileName(file.name);
      await initAudioGraph(buffer);
      
      addDebugLog('파일 로드 완료');
      
    } catch (error) {
      addDebugLog(`파일 로드 실패: ${error.message}`);
      setError('지원되지 않는 오디오 형식이거나 파일이 손상되었습니다.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 재생 시작
  const handlePlay = async () => {
    if (!audioContext || !audioBuffer || !analyserNode) return;
    
    try {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      // 기존 소스 노드가 있으면 정지
      if (sourceNode) {
        sourceNode.stop();
      }
      
      // 새 소스 노드 생성
      const newSourceNode = audioContext.createBufferSource();
      newSourceNode.buffer = audioBuffer;
      
      // 연결: source -> analyser
      newSourceNode.connect(analyserNode);
      
      // 현재 시간부터 재생 시작
      const playStartTime = audioContext.currentTime;
      newSourceNode.start(playStartTime, currentTime);
      
      // 재생 완료 시 처리
      newSourceNode.onended = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };
      
      setSourceNode(newSourceNode);
      setStartTime(playStartTime - currentTime);
      setIsPlaying(true);
      
      addDebugLog(`재생 시작 - 시작 지점: ${currentTime}초`);
      
    } catch (error) {
      addDebugLog(`재생 실패: ${error.message}`);
      setError('재생 중 오류가 발생했습니다.');
    }
  };
  
  // 일시정지
  const handlePause = () => {
    if (sourceNode && isPlaying) {
      sourceNode.stop();
      setSourceNode(null);
      setIsPlaying(false);
      addDebugLog('재생 일시정지');
    }
  };
  
  // 정지
  const handleStop = () => {
    if (sourceNode) {
      sourceNode.stop();
      setSourceNode(null);
    }
    setIsPlaying(false);
    setCurrentTime(0);
    addDebugLog('재생 정지');
  };
  
  // 시크
  const handleSeek = (seekTime) => {
    const wasPlaying = isPlaying;
    
    if (isPlaying) {
      handlePause();
    }
    
    setCurrentTime(seekTime);
    
    if (wasPlaying) {
      setTimeout(() => handlePlay(), 50);
    }
  };
  
  // 볼륨 변경
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (gainNode) {
      gainNode.gain.value = newVolume;
    }
  };
  
  // FFT 크기 변경
  const handleFftSizeChange = (newFftSize) => {
    setFftSize(newFftSize);
    if (analyserNode) {
      analyserNode.fftSize = newFftSize;
    }
  };
  
  // 스펙트럼 데이터 업데이트 핸들러
  const handleSpectrumUpdate = useCallback((data) => {
    setSpectrumData(data);
  }, []);
  
  return (
    <div className="audio-spectrum-container">
      <h1>{language === 'KR' ? '오디오 스펙트럼 분석기' : 'Audio Spectrum Analyzer'}</h1>
      
      {!audioBuffer ? (
        <AudioFileUploader
          onFileLoad={handleFileLoad}
          isLoading={isLoading}
          error={error}
          language={language}
        />
      ) : (
        <div className="audio-player-section">
          <div className="file-info">
            <h3>{fileName}</h3>
            <p>{language === 'KR' ? `길이: ${duration.toFixed(2)}초` : `Duration: ${duration.toFixed(2)}s`}</p>
          </div>
          
          
          
          <SpectrumVisualizer
            spectrumData={spectrumData}
            visualizationType={visualizationType}
            isPlaying={isPlaying}
            fftSize={fftSize}
            language={language}
          />
          
          <FrequencyAnalyzer
            analyserNode={analyserNode}
            isPlaying={isPlaying}
            onDataUpdate={handleSpectrumUpdate}
          />
          
          <button 
            onClick={() => {
              setAudioBuffer(null);
              setFileName('');
              handleStop();
            }}
            className="new-file-button"
          >
            {language === 'KR' ? '새 파일 선택' : 'Select New File'}
          </button>

          <div style={{width: "100%"}}>
            <ControlPanel
            onPlay={handlePlay}
            onPause={handlePause}
            onStop={handleStop}
            onSeek={handleSeek}
            onVolumeChange={handleVolumeChange}
            onFftSizeChange={handleFftSizeChange}
            onVisualizationTypeChange={setVisualizationType}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            volume={volume}
            fftSize={fftSize}
            visualizationType={visualizationType}
            language={language}
          />
          </div>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default AudioSpectrum;